/**
 * Named parameter table + formula engine (Faz 6.9, OCS named_parameters.rs).
 *
 * Each parameter holds an expression string evaluated at solve time. The
 * grammar mirrors OCS: numbers, parameter references, unary minus, + - * /
 * ^ and the builtins sqrt/abs/pow/min/max. Recursive-descent parser with
 * cycle detection across the reference graph (ParamError.Cycle path).
 */

/** One named parameter: expression + optional user note. */
export interface ParameterDef {
    expression: string;
    description?: string;
}

/** name -> definition. */
export type ParameterTable = Record<string, ParameterDef>;

/** Parse/eval error with an OCS-shaped kind (parse | ref | cycle). */
export class ParamError extends Error {
    kind: 'parse' | 'ref' | 'cycle';
    /** Cycle detection path (parameter names in evaluation order). */
    path?: string[];
    constructor(kind: 'parse' | 'ref' | 'cycle', message: string, path?: string[]) {
        super(message);
        this.kind = kind;
        this.path = path;
    }
}

// ---------------- AST ----------------

type Expr =
    | { t: 'num'; v: number }
    | { t: 'ref'; name: string }
    | { t: 'neg'; a: Expr }
    | { t: 'bin'; op: '+' | '-' | '*' | '/' | '^'; a: Expr; b: Expr }
    | { t: 'call'; fn: string; args: Expr[] };

const BUILTINS: ReadonlySet<string> = new Set(['sqrt', 'abs', 'pow', 'min', 'max']);

// ---------------- Tokenizer ----------------

interface Token {
    type: 'num' | 'ident' | 'op' | 'lparen' | 'rparen' | 'comma';
    text: string;
}

function tokenize(src: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    while (i < src.length) {
        const ch = src[i];
        if (/\s/.test(ch)) { i++; continue; }
        if (/[0-9.]/.test(ch)) {
            let j = i;
            while (j < src.length && /[0-9.]/.test(src[j])) j++;
            tokens.push({ type: 'num', text: src.slice(i, j) });
            i = j;
            continue;
        }
        if (/[A-Za-z_]/.test(ch)) {
            let j = i;
            while (j < src.length && /[A-Za-z0-9_]/.test(src[j])) j++;
            tokens.push({ type: 'ident', text: src.slice(i, j) });
            i = j;
            continue;
        }
        if ('+-*/^'.includes(ch)) { tokens.push({ type: 'op', text: ch }); i++; continue; }
        if (ch === '(') { tokens.push({ type: 'lparen', text: ch }); i++; continue; }
        if (ch === ')') { tokens.push({ type: 'rparen', text: ch }); i++; continue; }
        if (ch === ',') { tokens.push({ type: 'comma', text: ch }); i++; continue; }
        throw new ParamError('parse', `Unexpected character "${ch}"`);
    }
    return tokens;
}

// ---------------- Recursive-descent parser ----------------

class Parser {
    private pos = 0;
    constructor(private readonly tokens: Token[]) {}

    private peek(): Token | undefined { return this.tokens[this.pos]; }
    private next(): Token { const t = this.tokens[this.pos++]; if (!t) throw new ParamError('parse', 'Unexpected end of expression'); return t; }
    private expect(type: Token['type']): Token {
        const t = this.next();
        if (t.type !== type) throw new ParamError('parse', `Expected ${type}, got "${t.text}"`);
        return t;
    }

    parse(): Expr {
        const e = this.parseSum();
        if (this.peek()) throw new ParamError('parse', `Trailing input "${this.peek()!.text}"`);
        return e;
    }

    /** + - (lowest precedence) */
    private parseSum(): Expr {
        let a = this.parseProduct();
        for (;;) {
            const t = this.peek();
            if (t?.type === 'op' && (t.text === '+' || t.text === '-')) {
                this.next();
                a = { t: 'bin', op: t.text as '+' | '-', a, b: this.parseProduct() };
            } else return a;
        }
    }

    /** * / */
    private parseProduct(): Expr {
        let a = this.parseUnary();
        for (;;) {
            const t = this.peek();
            if (t?.type === 'op' && (t.text === '*' || t.text === '/')) {
                this.next();
                a = { t: 'bin', op: t.text as '*' | '/', a, b: this.parseUnary() };
            } else return a;
        }
    }

    /** unary minus */
    private parseUnary(): Expr {
        const t = this.peek();
        if (t?.type === 'op' && t.text === '-') {
            this.next();
            return { t: 'neg', a: this.parseUnary() };
        }
        return this.parsePower();
    }

    /** ^ (right-associative, binds tighter than unary base) */
    private parsePower(): Expr {
        const base = this.parseAtom();
        const t = this.peek();
        if (t?.type === 'op' && t.text === '^') {
            this.next();
            return { t: 'bin', op: '^', a: base, b: this.parseUnary() };
        }
        return base;
    }

    private parseAtom(): Expr {
        const t = this.next();
        if (t.type === 'num') {
            const v = Number(t.text);
            if (!Number.isFinite(v)) throw new ParamError('parse', `Bad number "${t.text}"`);
            return { t: 'num', v };
        }
        if (t.type === 'ident') {
            if (BUILTINS.has(t.text.toLowerCase())) {
                this.expect('lparen');
                const args: Expr[] = [this.parseSum()];
                for (;;) {
                    const c = this.peek();
                    if (c?.type === 'comma') { this.next(); args.push(this.parseSum()); } else break;
                }
                this.expect('rparen');
                return { t: 'call', fn: t.text.toLowerCase(), args };
            }
            return { t: 'ref', name: t.text };
        }
        if (t.type === 'lparen') {
            const e = this.parseSum();
            this.expect('rparen');
            return e;
        }
        throw new ParamError('parse', `Unexpected token "${t.text}"`);
    }
}

export function parseExpression(src: string): Expr {
    const trimmed = src.trim().replace(/^=/, '');
    if (!trimmed) throw new ParamError('parse', 'Empty expression');
    return new Parser(tokenize(trimmed)).parse();
}

// ---------------- Evaluation ----------------

function evalExpr(e: Expr, table: ParameterTable, chain: string[]): number {
    switch (e.t) {
        case 'num': return e.v;
        case 'neg': return -evalExpr(e.a, table, chain);
        case 'bin': {
            const a = evalExpr(e.a, table, chain);
            const b = evalExpr(e.b, table, chain);
            switch (e.op) {
                case '+': return a + b;
                case '-': return a - b;
                case '*': return a * b;
                case '/':
                    if (b === 0) throw new ParamError('parse', 'Division by zero');
                    return a / b;
                case '^': return Math.pow(a, b);
            }
            break;
        }
        case 'call': {
            const args = e.args.map((a) => evalExpr(a, table, chain));
            switch (e.fn) {
                case 'sqrt':
                    if (args[0] < 0) throw new ParamError('parse', 'sqrt of a negative value');
                    return Math.sqrt(args[0]);
                case 'abs': return Math.abs(args[0]);
                case 'pow': return Math.pow(args[0], args[1]);
                case 'min': return Math.min(...args);
                case 'max': return Math.max(...args);
            }
            throw new ParamError('parse', `Unknown function "${e.fn}"`);
        }
        case 'ref': {
            const name = e.name;
            if (chain.includes(name)) {
                throw new ParamError('cycle', `Parameter cycle: ${[...chain, name].join(' -> ')}`, [...chain, name]);
            }
            const def = table[name];
            if (!def) throw new ParamError('ref', `Unknown parameter "${name}"`);
            return evalExpr(parseExpression(def.expression), table, [...chain, name]);
        }
    }
    throw new ParamError('parse', 'Malformed expression');
}

/**
 * Evaluate one parameter by name. Throws ParamError on parse errors,
 * unknown references and definition cycles (Faz 6.9: the loop path never
 * explodes the solver — callers surface the message instead).
 */
export function evaluateParameter(name: string, table: ParameterTable): number {
    const def = table[name];
    if (!def) throw new ParamError('ref', `Unknown parameter "${name}"`);
    return evalExpr(parseExpression(def.expression), table, [name]);
}

/** Evaluate a raw expression string independent of the table. */
export function evaluateExpression(src: string, table: ParameterTable): number {
    return evalExpr(parseExpression(src), table, []);
}

/** Parse-check every parameter; returns the first failing name or null. */
export function firstInvalidParameter(table: ParameterTable): { name: string; error: ParamError } | null {
    for (const [name, def] of Object.entries(table)) {
        try {
            evaluateParameter(name, table);
        } catch (error) {
            return { name, error: error instanceof ParamError ? error : new ParamError('parse', String(error)) };
        }
    }
    return null;
}

/** Collect direct parameter references from an expression (cycle checks). */
export function expressionRefs(src: string): string[] {
    const refs: string[] = [];
    const walk = (e: Expr): void => {
        if (e.t === 'ref') refs.push(e.name);
        else if (e.t === 'neg') walk(e.a);
        else if (e.t === 'bin') { walk(e.a); walk(e.b); }
        else if (e.t === 'call') e.args.forEach(walk);
    };
    try { walk(parseExpression(src)); } catch { /* reported on evaluation */ }
    return refs;
}

/** True when evaluating `name` would create a definition cycle. */
export function createsCycle(name: string, expression: string, table: ParameterTable): boolean {
    const visited = new Set<string>();
    const stack = [name];
    const graph: Record<string, string[]> = {};
    const walk = (n: string): void => {
        if (visited.has(n)) return;
        visited.add(n);
        const def = table[n];
        graph[n] = n === name ? expressionRefs(expression) : (def ? expressionRefs(def.expression) : []);
        graph[n].forEach(walk);
    };
    walk(name);
    const check = (n: string, seen: Set<string>): boolean => {
        if (seen.has(n)) return true;
        const next = new Set(seen);
        next.add(n);
        return (graph[n] ?? []).some((r) => graph[r] !== undefined && check(r, next));
    };
    return check(name, new Set());
}
